import PropTypes from "prop-types";
import { useEffect, useState } from "react";

import HomeNavbar from "../components/candidate/HomeNavbar";
import { ExploreApi } from "../utils/exploreApi";

const JOB_TYPES = [
  { value: "", label: "All types" },
  { value: "full-time", label: "Full time" },
  { value: "intern", label: "Intern" },
  { value: "volunteering", label: "Volunteering" },
];

const JOBS_PAGE_SIZE = 6;

export default function ExploreBrowse() {
  const [jobs, setJobs] = useState([]);
  const [jobCount, setJobCount] = useState(0);
  const [jobSearch, setJobSearch] = useState("");
  const [jobType, setJobType] = useState("");
  const [jobOffset, setJobOffset] = useState(0);
  const [jobsLoading, setJobsLoading] = useState(true);

  const [businesses, setBusinesses] = useState([]);
  const [bizSearch, setBizSearch] = useState("");
  const [bizLoading, setBizLoading] = useState(true);

  // Reset to page 1 whenever the filters change.
  useEffect(() => {
    setJobOffset(0);
  }, [jobSearch, jobType]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setJobsLoading(true);
      ExploreApi.jobs({
        search: jobSearch,
        job_type: jobType,
        limit: JOBS_PAGE_SIZE,
        offset: jobOffset,
      })
        .then((data) => {
          setJobs(data.results || []);
          setJobCount(data.count || 0);
          return undefined;
        })
        .catch(() => undefined)
        .finally(() => setJobsLoading(false));
    }, 300);
    return () => clearTimeout(timeout);
  }, [jobSearch, jobType, jobOffset]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setBizLoading(true);
      ExploreApi.businesses({ search: bizSearch })
        .then((data) => {
          setBusinesses(data.results || []);
          return undefined;
        })
        .catch(() => undefined)
        .finally(() => setBizLoading(false));
    }, 300);
    return () => clearTimeout(timeout);
  }, [bizSearch]);

  return (
    <>
      <HomeNavbar />
      <div className="mt-16 min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <div className="mx-auto max-w-5xl">
          <h1 className="text-3xl font-semibold text-gray-800 dark:text-gray-100">
            Explore
          </h1>
          <p className="mt-1 mb-6 text-gray-600 dark:text-gray-400">
            Open roles and small businesses looking for help, beyond your
            curated matches.
          </p>

          <section className="mb-10">
            <h2 className="mb-3 text-lg font-semibold text-gray-800 dark:text-gray-100">
              Open roles
            </h2>
            <div className="mb-4 flex flex-col gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm sm:flex-row sm:items-center">
              <input
                className="flex-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
                placeholder="Search open roles..."
                type="text"
                value={jobSearch}
                onChange={(e) => setJobSearch(e.target.value)}
              />
              <select
                aria-label="Job type"
                className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
                value={jobType}
                onChange={(e) => setJobType(e.target.value)}
              >
                {JOB_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            {jobsLoading ? (
              <p className="py-6 text-center text-gray-400 dark:text-gray-500">
                Loading...
              </p>
            ) : jobs.length === 0 ? (
              <p className="py-6 text-center text-gray-400 dark:text-gray-500">
                No open roles match your filters.
              </p>
            ) : (
              <>
                <div className="mb-3 flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                  <p>
                    {jobCount} role{jobCount === 1 ? "" : "s"}
                  </p>
                  <p>
                    {jobOffset + 1}–
                    {Math.min(jobOffset + JOBS_PAGE_SIZE, jobCount)} of{" "}
                    {jobCount}
                  </p>
                </div>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {jobs.map((job) => (
                    <JobCard key={job.id} job={job} />
                  ))}
                </div>
                <div className="mt-4 flex items-center justify-center gap-3">
                  <button
                    className="rounded border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={jobOffset === 0}
                    type="button"
                    onClick={() =>
                      setJobOffset((prev) => Math.max(0, prev - JOBS_PAGE_SIZE))
                    }
                  >
                    Previous
                  </button>
                  <button
                    className="rounded border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={jobOffset + JOBS_PAGE_SIZE >= jobCount}
                    type="button"
                    onClick={() =>
                      setJobOffset((prev) => prev + JOBS_PAGE_SIZE)
                    }
                  >
                    Next
                  </button>
                </div>
              </>
            )}
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-gray-800 dark:text-gray-100">
              Businesses looking for help
            </h2>
            <div className="mb-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm">
              <input
                className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
                placeholder="Search businesses..."
                type="text"
                value={bizSearch}
                onChange={(e) => setBizSearch(e.target.value)}
              />
            </div>
            {bizLoading ? (
              <p className="py-6 text-center text-gray-400 dark:text-gray-500">
                Loading...
              </p>
            ) : businesses.length === 0 ? (
              <p className="py-6 text-center text-gray-400 dark:text-gray-500">
                No businesses match your search.
              </p>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {businesses.map((biz) => (
                  <BusinessCard key={biz.id} business={biz} />
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </>
  );
}

function JobCard({ job }) {
  return (
    <div className="flex flex-col rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm">
      <div className="mb-2 flex items-center justify-between">
        <span className="rounded-full bg-blue-100 dark:bg-blue-900/40 px-2 py-0.5 text-xs font-semibold capitalize text-blue-700 dark:text-blue-300">
          {job.job_type}
        </span>
        {job.is_remote ? (
          <span className="rounded-full bg-green-100 dark:bg-green-900/40 px-2 py-0.5 text-xs font-semibold text-green-700 dark:text-green-300">
            Remote
          </span>
        ) : null}
      </div>
      <h3 className="font-semibold text-gray-800 dark:text-gray-100">
        {job.title}
      </h3>
      <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
        {job.company_name}
        {job.city ? ` · ${job.city}, ${job.state}` : ""}
      </p>
      <p className="mt-2 line-clamp-2 text-sm text-gray-600 dark:text-gray-400">
        {job.description}
      </p>
    </div>
  );
}

JobCard.propTypes = {
  job: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    title: PropTypes.string.isRequired,
    job_type: PropTypes.string,
    is_remote: PropTypes.bool,
    company_name: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    description: PropTypes.string,
  }).isRequired,
};

function BusinessCard({ business }) {
  return (
    <div className="flex flex-col rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm">
      <h3 className="font-semibold text-gray-800 dark:text-gray-100">
        {business.company_name}
      </h3>
      {business.company_address ? (
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
          {business.company_address}
        </p>
      ) : null}
      <p className="mt-2 line-clamp-3 text-sm text-gray-600 dark:text-gray-400">
        {business.problems_facing}
      </p>
      {business.seeking_volunteers ? (
        <span className="mt-3 inline-block w-fit rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
          Seeking volunteers
        </span>
      ) : null}
    </div>
  );
}

BusinessCard.propTypes = {
  business: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    company_name: PropTypes.string,
    company_address: PropTypes.string,
    problems_facing: PropTypes.string,
    seeking_volunteers: PropTypes.bool,
  }).isRequired,
};
